import React, { useEffect, useId, useRef, useState } from 'react';
import { Box, Button, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { MillosMark } from '../icons';
import Eyebrow from '../Eyebrow';
import { marketingDarkPalette, marketingDarkStyles } from '../../../theme_landing_page_2';

const ECOSYSTEM_COLORS = {
  surfaceDark: marketingDarkPalette.surface,
  surfaceDarkElevated: marketingDarkPalette.elevated,
  surfaceDarkSoft: marketingDarkPalette.hover,
  onDark: marketingDarkPalette.text,
  onDarkSoft: marketingDarkPalette.muted,
  primary: marketingDarkPalette.accent,
  surfaceCreamStrong: marketingDarkPalette.blue,
  accentTeal: marketingDarkPalette.accent,
  canvas: marketingDarkPalette.elevated,
};

type Category = 'certificates' | 'accounting' | 'orchestration' | 'insight';
interface Feature {
  label: string;
  category: Category;
  x: number;
  y: number;
  description: string;
}

const categories: { id: Category; label: string; colour: keyof typeof ECOSYSTEM_COLORS; description: string; compactDescription: string }[] = [
  { id: 'certificates', label: 'Certificates', colour: 'primary', compactDescription: 'MillOS tracks expiry dates, arranges renewals and files your certificates.', description: 'MillOS reads your certificates, tracks their expiry dates and plans renewals. It arranges inspection visits with engineers and tenants, then files the new certificate against the right property.' },
  { id: 'accounting', label: 'Accounting', colour: 'surfaceCreamStrong', compactDescription: 'MillOS matches rent, organises expenses and prepares quarterly figures for your accountant.', description: 'MillOS matches rent payments to the right flat and organises invoices and expenses by property. Your quarterly figures are prepared to share with your accountant for Making Tax Digital.' },
  { id: 'orchestration', label: 'AI orchestration', colour: 'accentTeal', compactDescription: 'AI coordinates engineers and tenants, follows up and confirms inspection visits.', description: 'AI coordinates inspection dates with engineers and tenants, follows up on outstanding replies and sends calendar invitations once the visit is agreed.' },
  { id: 'insight', label: 'Portfolio insight', colour: 'onDark', compactDescription: 'See compliance, finances and activity across your portfolio in one place.', description: 'See certificate status and accounting records across your portfolio in one place. Reports and activity history help you understand what needs attention and what has already been done.' },
];

const orbitPosition = (slot: number, ring: 'inner' | 'outer') => {
  const angle = -Math.PI / 2 + slot * 2 * Math.PI / (ring === 'inner' ? 6 : 9);
  return {
    x: 550 + Math.cos(angle) * (ring === 'inner' ? 255 : 460),
    y: 300 + Math.sin(angle) * (ring === 'inner' ? 125 : 260),
  };
};
const features: Feature[] = [
  { label: 'Reads certificates', category: 'certificates', ...orbitPosition(0, 'outer'), description: 'AI reads the incoming certificate and matches it to the property.' },
  { label: 'Expiry tracking', category: 'certificates', ...orbitPosition(4, 'outer'), description: 'The expiry date becomes part of the property record and renewal planning.' },
  { label: 'Evidence vault', category: 'certificates', ...orbitPosition(0, 'inner'), description: 'The certificate and its version history stay together as property evidence.' },
  { label: 'Tenant access', category: 'certificates', ...orbitPosition(3, 'inner'), description: 'Tenant availability is connected to the renewal inspection.' },
  { label: 'Books the engineer', category: 'orchestration', ...orbitPosition(1, 'outer'), description: 'AI coordinates the renewal visit with the engineer.' },
  { label: 'Auto follow-ups', category: 'orchestration', ...orbitPosition(5, 'outer'), description: 'AI follows up on outstanding scheduling messages.' },
  { label: 'WhatsApp & email', category: 'orchestration', ...orbitPosition(1, 'inner'), description: 'Workflow messages stay connected to the property and the job.' },
  { label: 'Calendar invites', category: 'orchestration', ...orbitPosition(4, 'inner'), description: 'The agreed inspection details become calendar invitations.' },
  { label: 'Expense capture', category: 'accounting', ...orbitPosition(2, 'outer'), description: 'The inspection invoice is read and recorded against the property.' },
  { label: 'Accountant pack', category: 'accounting', ...orbitPosition(7, 'outer'), description: 'Property income and expenses are prepared to share with your accountant.' },
  { label: 'Open Banking rent', category: 'accounting', ...orbitPosition(2, 'inner'), description: 'Read-only bank data connects rent payments to the right property.' },
  { label: 'MTD-ready figures', category: 'accounting', ...orbitPosition(5, 'inner'), description: 'Property finances are organised into quarterly figures for MTD preparation.' },
  { label: 'Compliance score', category: 'insight', ...orbitPosition(3, 'outer'), description: 'Certificate status feeds back into the portfolio compliance view.' },
  { label: 'Full audit trail', category: 'insight', ...orbitPosition(6, 'outer'), description: 'Recorded activity preserves the history of the job.' },
  { label: 'Live reports', category: 'insight', ...orbitPosition(8, 'outer'), description: 'Updated records are brought together in portfolio reports.' },
];
const MillosOrbit: React.FC = () => {
  const theme = useTheme();
  const t = ECOSYSTEM_COLORS;
  const id = useId();
  const rootRef = useRef<HTMLElement>(null);
  const compact = useMediaQuery('(max-width:1100px)');
  const tablet = useMediaQuery('(min-width:640px) and (max-width:1100px)');
  const portrait = useMediaQuery('(orientation: portrait)');
  const circular = tablet && !portrait;
  const [category, setCategory] = useState<Category>('certificates');
  const [feature, setFeature] = useState<Feature | null>(null);
  const [visible, setVisible] = useState(false);
  const [compactFocused, setCompactFocused] = useState(false);
  const current = feature;
  const activeCategory = current?.category ?? category;
  const group = categories.find((entry) => entry.id === activeCategory)!;
  const colour = t[group.colour];

  useEffect(() => {
    let inView = typeof IntersectionObserver === 'undefined';
    const sync = () => setVisible(inView && document.visibilityState !== 'hidden');
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      sync();
    });
    if (rootRef.current) observer?.observe(rootRef.current);
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  const chooseCategory = (value: Category) => {
    setCompactFocused(true);
    setFeature(null);
    setCategory(value);
  };
  const chooseFeature = (value: Feature) => {
    setCompactFocused(true);
    setCategory(value.category);
    setFeature(value);
  };
  const resetCompact = () => {
    setCompactFocused(false);
    setFeature(null);
  };
  const description = compact && !compactFocused
    ? 'Choose a category to explore its connected features.'
    : current?.description ?? (compact ? group.compactDescription : group.description);
  const featureButton = (item: Feature, diagram: boolean) => {
    const itemGroup = categories.find((entry) => entry.id === item.category)!;
    const itemColour = t[itemGroup.colour];
    const on = (!compact || compactFocused) && item.category === activeCategory;
    return (
      <Button
        key={item.label}
        size="small"
        variant="outlined"
        aria-pressed={current === item}
        aria-describedby={`${id}-description`}
        onClick={() => chooseFeature(item)}
        sx={{
          width: diagram ? '100%' : 'auto', minWidth: 0, height: '100%',
          px: diagram ? 1 : 1.5, py: diagram ? 0.75 : 1, borderRadius: 8, whiteSpace: diagram ? 'nowrap' : 'normal',
          typography: 'bodySm', fontWeight: 600, textTransform: 'none',
          backgroundColor: t.surfaceDarkElevated,
          color: on ? t.onDark : t.onDarkSoft,
          borderColor: on ? itemColour : alpha(t.onDarkSoft, 0.25),
          boxShadow: current === item ? `0 0 0 3px ${alpha(itemColour, 0.2)}` : 'none',
          '&:hover': { backgroundColor: t.surfaceDarkSoft, borderColor: itemColour },
          '&:focus-visible': { outline: `2px solid ${t.onDark}`, outlineOffset: 3 },
        }}
      >
        <Box component="span" sx={{ width: 6, height: 6, mr: 1, flexShrink: 0, borderRadius: '50%', backgroundColor: on ? itemColour : t.onDarkSoft }} />
        {item.label}
      </Button>
    );
  };

  return (
    <Box component="section" ref={rootRef} data-millos-orbit aria-labelledby={`${id}-heading`} sx={{
      ...marketingDarkStyles, p: 1.5, scrollMarginTop: '80px',
    }}>
      <Box onClick={(event: React.MouseEvent) => {
        if (compact && event.target instanceof Element && !event.target.closest('button, [role="tab"]')) resetCompact();
      }} onKeyDown={(event: React.KeyboardEvent) => {
        if (compact && event.key === 'Escape') resetCompact();
      }} sx={{ maxWidth: 1440, mx: 'auto', p: compact ? '24px 16px' : tablet ? '16px 24px' : 4, minHeight: 'calc(100svh - 104px)', boxSizing: 'border-box', borderRadius: 4, backgroundColor: t.surfaceDark, color: t.onDark,
        ...(compact || tablet ? { display: 'flex', flexDirection: 'column' } : {}),
        ...(tablet ? { height: 'calc(100svh - 104px)', '& > :first-child': { flexShrink: 0 } } : {}),
      }}>
        <Box sx={{ textAlign: 'center', maxWidth: 960, mx: 'auto' }}>
          <Eyebrow variant="onImage">The MillOS ecosystem</Eyebrow>
          <Typography component="h2" variant="displayLg" id={`${id}-heading`} sx={{ mt: 1.5, fontSize: compact ? 'clamp(1.6rem, 6.5vw, 2rem)' : tablet ? 'clamp(1.8rem, 3.5vw, 2.4rem)' : theme.typography.displayLg.fontSize }}>
            Every property. Every workflow. Connected.
          </Typography>
        </Box>

        {compact ? (
          <Box sx={{ position: 'relative', flex: tablet ? '1 0 180px' : '1 0 320px', width: '100%', my: 3, containerType: 'size' }}>
          <Box role="group" aria-label={compactFocused ? `${group.label} connected features` : 'All fifteen connected features'} data-ecosystem-focused={compactFocused} sx={{
            position: 'absolute', width: circular ? 'min(100cqw, 100cqh)' : '100%',
            height: circular ? 'min(100cqw, 100cqh)' : '100%', maxWidth: tablet ? 'none' : 430,
            left: '50%', top: '50%',
            transform: 'translate(-50%, -50%)',
            mx: 'auto',
            '@keyframes compactEcosystemSpin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
            '@keyframes compactEcosystemOrbit': { from: { offsetDistance: '0%' }, to: { offsetDistance: '100%' } },
            '& [data-ecosystem-motion]': { animationPlayState: visible && !compactFocused ? 'running' : 'paused' },
            '@media (prefers-reduced-motion: reduce)': { '& [data-ecosystem-motion]': { animation: 'none' } },
          }}>
            <Box component="svg" aria-hidden="true" viewBox={circular ? '0 0 320 320' : '0 0 320 280'} preserveAspectRatio="none" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
              {(circular ? [{ rx: 137.6, ry: 137.6 }, { rx: 80, ry: 80 }] : [{ rx: 109.12, ry: 138.6 }, { rx: 63.36, ry: 89.32 }]).map(({ rx, ry }) => (
                <g key={rx} fill="none">
                  <ellipse cx="160" cy={circular ? 160 : 140} rx={rx} ry={ry} stroke={alpha(t.onDarkSoft, 0.22)} strokeWidth="1.5" strokeDasharray="0 7" strokeLinecap="round" />
                </g>
              ))}
            </Box>
            <Box aria-hidden="true" sx={{
              position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
              width: 60, height: 60, borderRadius: '50%', bgcolor: t.canvas, display: 'grid', placeItems: 'center',
            }}>
              <Box data-ecosystem-motion sx={{ display: 'flex', animation: 'compactEcosystemSpin 26s linear infinite' }}><MillosMark size={52} /></Box>
            </Box>
            {features.map((item) => {
              const x = item.x - 550;
              const y = item.y - 300;
              const outer = Math.abs(x * x / (460 * 460) + y * y / (260 * 260) - 1) < 0.01;
              const angle = Math.atan2(y / (outer ? 260 : 125), x / (outer ? 460 : 255));
              const phase = ((angle / (Math.PI * 2)) + 1) % 1;
              return (
                <Box key={item.label} data-ecosystem-motion data-floating-feature
                  sx={{
                    position: 'absolute', left: 0, top: 0, width: '24%', maxWidth: 92, height: 24,
                    offsetPath: circular
                      ? outer ? 'circle(43% at 50% 50%)' : 'circle(25% at 50% 50%)'
                      : outer ? 'ellipse(34.1% 49.5% at 50% 50%)' : 'ellipse(19.8% 31.9% at 50% 50%)',
                    offsetRotate: '0deg', offsetAnchor: 'center',
                    animation: `compactEcosystemOrbit 120s linear ${-phase * 120}s infinite`,
                    zIndex: compactFocused && item.category === activeCategory ? 2 : 1,
                    transform: `scale(${compactFocused && item.category === activeCategory ? 1.5 : 0.85})`,
                    transition: 'transform 200ms ease',
                    '@media (prefers-reduced-motion: reduce)': { offsetDistance: `${phase * 100}%` },
                    '& > button': { width: '100%', minHeight: 24, px: '4px', py: '2px', fontSize: 8, lineHeight: 1.1, '& > span': { width: 3, height: 3, mr: '3px' } },
                  }}>{featureButton(item, false)}</Box>
              );
            })}
          </Box>
          </Box>
        ) : (
          <Box sx={tablet ? { position: 'relative', flex: '1 0 180px', my: 2, width: '100%' } : {}}>
          <Box component="svg" viewBox="0 0 1100 600" preserveAspectRatio={tablet ? 'none' : 'xMidYMid meet'} role="group" aria-label="Fifteen connected features on two rotating rings" sx={{ position: tablet ? 'absolute' : 'relative', inset: tablet ? 0 : undefined, top: tablet ? 0 : 42, display: 'block', width: tablet ? '100%' : '85%', maxWidth: tablet ? 'none' : 935, height: tablet ? '100%' : undefined, maxHeight: tablet ? 'none' : 'max(180px, calc(100svh - 460px))', transform: tablet ? 'none' : 'scale(1.15)', overflow: 'visible', mx: 'auto', mt: tablet ? 0 : 1, animation: 'ecosystemRingRotation 240s linear infinite', animationPlayState: visible ? 'running' : 'paused', '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}>
            <style>{`
              @property --ecosystem-ring-angle{syntax:"<angle>";inherits:true;initial-value:0deg}
              @keyframes ecosystemRingRotation{from{--ecosystem-ring-angle:0deg}to{--ecosystem-ring-angle:360deg}}
              @keyframes ecosystemLogoRotation{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
            `}</style>
            <g aria-hidden="true" fill="none" stroke={alpha(t.onDarkSoft, 0.28)} strokeWidth="2" strokeDasharray="0 9" strokeLinecap="round">
              <ellipse cx="550" cy="300" rx="460" ry="260" />
              <ellipse cx="550" cy="300" rx="255" ry="125" />
            </g>
            {!tablet && (
              <>
                <circle cx="550" cy="300" r="55.2805" fill={t.canvas} stroke={alpha(t.onDark, 0.3)} />
                <foreignObject x="492.5" y="242.5" width="115" height="115" aria-hidden="true">
                  <Box sx={{ display: 'grid', placeItems: 'center', width: '100%', height: '100%', '& > svg': { animation: 'ecosystemLogoRotation 26s linear infinite', animationPlayState: visible ? 'running' : 'paused', '@media (prefers-reduced-motion: reduce)': { animation: 'none' } } }}><MillosMark size={104.742} /></Box>
                </foreignObject>
              </>
            )}
            {features.map((item) => {
              const x = item.x - 550;
              const y = item.y - 300;
              const outer = Math.abs(x * x / (460 * 460) + y * y / (260 * 260) - 1) < 0.01;
              const ratio = outer ? 260 / 460 : 125 / 255;
              return (
                <g key={item.label} transform={`translate(550 300) scale(1 ${ratio})`}>
                  <g style={{ transform: 'rotate(var(--ecosystem-ring-angle, 0deg))' }}>
                    <g transform={`translate(${x} ${y / ratio})`}>
                      <g style={{ transform: 'rotate(calc(-1 * var(--ecosystem-ring-angle, 0deg)))' }}>
                        <g transform={`scale(1 ${1 / ratio})`}>
                          <foreignObject x="-88.2" y="-18" width="176.4" height="36">
                            {featureButton(item, true)}
                          </foreignObject>
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              );
            })}
          </Box>
          {tablet && (
            <Box aria-hidden="true" data-ecosystem-center sx={{
              position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
              width: 72, height: 72, borderRadius: '50%', bgcolor: t.canvas,
              border: `1px solid ${alpha(t.onDark, 0.3)}`, boxSizing: 'border-box',
              display: 'grid', placeItems: 'center', pointerEvents: 'none',
              '& > svg': {
                animation: 'ecosystemLogoRotation 26s linear infinite',
                animationPlayState: visible ? 'running' : 'paused',
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              },
            }}><MillosMark size={68} /></Box>
          )}
          </Box>
        )}

        <Tabs value={compact && !compactFocused ? false : activeCategory} onChange={(_, value: Category) => chooseCategory(value)}
          aria-label="Ecosystem categories" variant={compact ? 'standard' : 'scrollable'} scrollButtons={compact ? false : 'auto'}
          sx={{ position: 'relative', top: compact || tablet ? 0 : 74, mt: compact || tablet ? 'auto' : 2, flexShrink: 0, mx: 'auto', maxWidth: '100%', width: compact ? '100%' : 'fit-content',
            '& .MuiTabs-list': compact ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 } : { gap: tablet ? 1 : 2 },
            '& .MuiTabs-indicator': { backgroundColor: colour, ...(compact ? { display: 'none' } : {}) },
            '& .MuiTab-root': { color: compact ? t.onDark : t.onDarkSoft, textTransform: 'none', typography: compact || tablet ? 'bodySm' : 'bodyMd', px: compact || tablet ? 1 : 3, py: compact ? 0.75 : 1.5, minWidth: 0, minHeight: compact ? 0 : 52,
              ...(compact ? { border: `1px solid ${alpha(t.onDarkSoft, 0.25)}`, borderRadius: '12px', '&.Mui-selected': { color: colour, borderColor: colour, bgcolor: t.surfaceDarkElevated } } : { '&.Mui-selected': { color: colour } }),
              '&:focus-visible': { outline: `2px solid ${t.onDark}`, outlineOffset: -2 } } }}>
          {categories.map((entry) => <Tab key={entry.id} id={`${id}-${entry.id}`} aria-controls={`${id}-panel`} value={entry.id} label={entry.label} />)}
        </Tabs>
        <Box id={`${id}-panel`} role={compact && !compactFocused ? undefined : 'tabpanel'} aria-labelledby={compact && !compactFocused ? undefined : `${id}-${activeCategory}`} sx={{ position: 'relative', top: compact || tablet ? 0 : 74, textAlign: 'center', minHeight: compact ? 0 : 64, mt: 2 }}>
          <Typography id={`${id}-description`} role="status" aria-live="polite" sx={{ color: t.onDarkSoft, typography: 'bodySm', maxWidth: 1000, mx: 'auto' }}>
            {description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default MillosOrbit;
